import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { getDataStep, getListPrestasi } from "@/services/getData";
import { DataPrestasi } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  Award,
  ChevronRight,
  Edit,
  Eye,
  FileText,
  Info,
  Loader2,
  Medal,
  Plus,
  Trash2,
  Trophy,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import Form from "./partial/Form";

const skipStepPrestasi = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/prestasi/skip?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data;
};

export default function Prestasi({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const { timePassedActive } = useTimePeriode();

  const {
    data: listDataPrestasi,
    isPending: isPendingListDataPrestasi,
    isFetched: isFetchedListDataPrestasi,
  } = useQuery({
    queryKey: ["listPrestasi"],
    queryFn: () => getListPrestasi(sessionPeriode?.id_sys_periode || ""),
  });

  const { data: dataStep, isPending: isPendingDataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });

  const [showDetail, setShowDetail] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSkipPrestasi, setShowSkipPrestasi] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataPrestasi | null>(null);

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: skipStepPrestasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Berhasil melewati tahap prestasi.");
      setShowSkipPrestasi(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(err.response?.data?.message || "Terjadi kesalahan.");
    },
  });

  const handleSkipPrestasi = () => mutate(idDataPendaftaran);

  if (isPendingListDataPrestasi || isPendingDataStep) return <SkeletonTable />;

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  // Render Component for Empty State / Initial Choice
  const InitialChoice = () => (
    <div className="mt-6 flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/20">
      <div className="p-4 bg-white dark:bg-slate-900 rounded-full shadow-sm mb-4">
        <Trophy className="w-10 h-10 text-amber-500" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
        Apakah Anda Memiliki Prestasi?
      </h3>
      <p className="text-slate-500 text-sm max-w-md mb-6">
        Jika Anda memiliki sertifikat kejuaraan atau penghargaan
        akademik/non-akademik, silakan tambahkan di sini untuk menambah nilai
        seleksi.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <div
          className="flex-1 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-all flex flex-col items-center gap-2 group"
          onClick={() => setShowInsert(true)}
        >
          <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-full text-blue-600 group-hover:scale-110 transition-transform">
            <Plus className="w-5 h-5" />
          </div>
          <span className="font-semibold text-sm">Ya, Saya Punya</span>
        </div>

        <div
          className="flex-1 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex flex-col items-center gap-2 group"
          onClick={() => setShowSkipPrestasi(true)}
        >
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 group-hover:scale-110 transition-transform">
            <ChevronRight className="w-5 h-5" />
          </div>
          <span className="font-semibold text-sm text-slate-600 dark:text-slate-400">
            Tidak Ada
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <Medal className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Data Prestasi
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Unggah bukti pencapaian terbaik Anda (Max 3).
          </p>
        </div>
      </div>

      {/* CONTENT LOGIC */}
      {isFetchedListDataPrestasi &&
      listDataPrestasi?.length === 0 &&
      dataStep?.data_prestasi !== 1 &&
      !isDisableInput ? (
        <InitialChoice />
      ) : (
        <>
          <Alert className="bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
            <Info className="h-4 w-4" />
            <AlertTitle className="font-bold">Ketentuan</AlertTitle>
            <AlertDescription className="text-xs">
              Maksimal 3 data prestasi terbaik. File bukti harus berformat
              PDF/Gambar yang jelas.
            </AlertDescription>
          </Alert>

          {/* LIST PRESTASI (Card View) */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Add New Card Button */}
            {!isDisableInput &&
              listDataPrestasi &&
              listDataPrestasi.length < 3 && (
                <button
                  onClick={() => setShowInsert(true)}
                  className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group min-h-[200px]"
                >
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                    <Plus className="w-6 h-6" />
                  </div>
                  <span className="font-semibold text-slate-500 group-hover:text-blue-600 text-sm">
                    Tambah Prestasi
                  </span>
                </button>
              )}

            {listDataPrestasi?.map((item: DataPrestasi, index: number) => (
              <div
                key={item.id_data_prestasi}
                className="relative flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden group hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                    >
                      #{index + 1}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/30">
                      {item.jenis_prestasi?.nama_jenis_prestasi}
                    </Badge>
                  </div>

                  {/* Inline Action Buttons (Edit & Delete) */}
                  {!isDisableInput && (
                    <div className="flex items-center gap-1 -mr-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20"
                        title="Edit Prestasi"
                        onClick={() => {
                          setShowEdit(true);
                          setSelectedRow(item);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                        title="Hapus Prestasi"
                        onClick={() => {
                          setShowDelete(true);
                          setSelectedRow(item);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3 text-sm flex-1">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Tingkat & Capaian
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200 line-clamp-2">
                      {item.jenjang_prestasi?.nama_jenjang_prestasi} —{" "}
                      {item.capaian_prestasi?.nama_capaian_prestasi}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Tahun
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200">
                      {item.tahun_prestasi}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Penyelenggara
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200 line-clamp-1">
                      {item.lembaga_pemberi_prestasi}
                    </p>
                  </div>
                </div>

                {/* Card Footer (Actions) */}
                <div className="p-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs h-8"
                    onClick={() => {
                      setShowDetail(true);
                      setSelectedRow(item);
                    }}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Detail
                  </Button>
                  {item.path_bukti_prestasi ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full text-xs h-8 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
                      onClick={() =>
                        window.open(item.path_bukti_prestasi || "")
                      }
                    >
                      <FileText className="w-3.5 h-3.5 mr-1.5" /> Bukti
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs h-8 text-slate-400 cursor-not-allowed"
                      disabled
                    >
                      No File
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* --- MODALS --- */}

      {/* Modal Insert */}
      <Modal
        show={showInsert}
        onClose={() => setShowInsert(false)}
        className="lg:w-[40vw] w-[95vw]"
      >
        <Form
          method="POST"
          closeModal={() => setShowInsert(false)}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Modal Edit */}
      <Modal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        className="lg:w-[40vw] w-[95vw]"
      >
        <Form
          method="PATCH"
          closeModal={() => setShowEdit(false)}
          selectedRow={selectedRow}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Modal Delete */}
      <Modal
        show={showDelete}
        onClose={() => setShowDelete(false)}
        className="lg:w-[40vw] w-[95vw]"
      >
        <Form
          method="DELETE"
          closeModal={() => setShowDelete(false)}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* Modal Detail View */}
      <Modal
        show={showDetail}
        onClose={() => setShowDetail(false)}
        className="lg:w-[40vw] w-[95vw]"
      >
        <FormHeader title="Detail Prestasi" icon={<Award />} />
        <div className="p-4 space-y-4">
          {selectedRow && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Jenis</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.jenis_prestasi?.nama_jenis_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Tingkat</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.jenjang_prestasi?.nama_jenjang_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Capaian</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.capaian_prestasi?.nama_capaian_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Tahun</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.tahun_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Penyelenggara</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.lembaga_pemberi_prestasi}
                </span>
              </div>
              {/* <div className="grid grid-cols-3 gap-2">
                      <span className="text-slate-500">Deskripsi</span>
                      <span className="col-span-2 text-slate-700">{selectedRow.deskripsi || "-"}</span>
                   </div> */}
            </div>
          )}
          <div className="pt-4 text-right">
            <Button variant="outline" onClick={() => setShowDetail(false)}>
              Tutup
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Skip Confirmation */}
      <Modal show={showSkipPrestasi} onClose={() => setShowSkipPrestasi(false)}>
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            Konfirmasi Tidak Ada Prestasi
          </h3>
          <p className="text-slate-500 text-sm mb-6">
            Apakah Anda yakin tidak memiliki riwayat prestasi? <br />
            Anda akan melewatkan kesempatan menambah poin seleksi dari jalur
            prestasi.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => setShowSkipPrestasi(false)}
              disabled={isPending}
            >
              Batal
            </Button>
            <Button
              onClick={handleSkipPrestasi}
              disabled={isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white hover:text-white"
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                "Ya, Saya Yakin"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
